import React, {useState, useEffect, useRef, useCallback, useMemo} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../features/auth/contexts/AuthContext';
import LanguageSwitcher from './LanguageSwitcher';
import { NotificationBell } from './NotificationBell';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1100;
  background: var(--color-white);
  box-shadow: var(--shadow-base);
  border-bottom: 1px solid var(--color-gray-200);
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  display: flex;
  align-items: center;
  height: 80px;
`;

const Logo = styled(Link)`
  font-family: var(--font-serif);
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-gray-800);
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.9rem;
  border-radius: var(--border-radius-md);
  background: transparent;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    color: var(--color-primary);
    background: var(--color-white);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2);
  }
`;

const LogoIcon = styled.div`
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%);
  border-radius: var(--border-radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: bold;
  color: var(--color-white);
`;

const MenuBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex-wrap: nowrap;
  background: var(--color-gray-50);
  padding: 0.25rem;
  border-radius: var(--border-radius-lg);
  border: 1px solid var(--color-gray-200);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin: 0 1rem;
  flex: 1;
  overflow: visible; /* Allow dropdowns to show outside container */

  @media (max-width: 768px) {
    display: none; /* Hide menu bar on mobile */
  }
`;

const Navigation = styled.nav<{ hasAuthButtons?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${props => props.hasAuthButtons ? 'space-between' : 'flex-end'};
  gap: 0.5rem;
  flex-wrap: nowrap;
  overflow: visible;
`;

const NavigationItems = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: nowrap;
`;

const RightItems = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: nowrap;
`;

const MoreMenuContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const MoreMenuButton = styled.button`
  color: var(--color-gray-700);
  text-decoration: none;
  font-weight: 500;
  font-family: var(--font-sans);
  transition: all 0.2s ease;
  white-space: nowrap;
  padding: 0.5rem 0.8rem;
  border-radius: var(--border-radius-md);
  background: transparent;
  border: 1px solid transparent;
  font-size: 0.95rem;
  cursor: pointer;

  &:hover {
    color: var(--color-primary);
    background: var(--color-white);
    border-color: var(--color-primary);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }
`;

const MoreMenuDropdown = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.25rem;
  background: var(--color-white);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--border-radius-md);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15), 0 4px 6px rgba(0, 0, 0, 0.1);
  min-width: 200px;
  z-index: 1120;
  opacity: ${props => props.isOpen ? '1' : '0'};
  transform: ${props => props.isOpen ? 'translateY(0) scale(1)' : 'translateY(-8px) scale(0.95)'};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.2s ease;
  backdrop-filter: blur(8px);
`;

const MoreMenuLink = styled(Link)`
  display: block;
  padding: 0.75rem 1rem;
  color: var(--color-gray-700);
  text-decoration: none;
  font-family: var(--font-sans);
  font-weight: 500;
  border-bottom: 1px solid var(--color-gray-100);
  transition: background-color 0.2s ease;

  &:hover {
    background: var(--color-gray-50);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 44px;
  height: 44px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  position: relative;
  z-index: 1111;
  border-radius: var(--border-radius-md);
  transition: background-color 0.2s ease;
  margin-left: auto;

  &:hover {
    background: var(--color-gray-100);
  }

  &:focus {
    outline: none;
    background: var(--color-gray-100);
    box-shadow: 0 0 0 2px var(--color-primary);
  }

  @media (max-width: 768px) {
    display: flex !important;
  }
`;

const BurgerLine = styled.div<{ isOpen: boolean }>`
  width: 24px;
  height: 3px;
  background-color: var(--color-gray-800);
  margin: 3px 0;
  border-radius: 2px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: center;
  
  &:nth-child(1) {
    transform: ${props => props.isOpen ? 'rotate(-45deg) translate(-5px, 6px)' : 'translateX(0)'};
  }
  
  &:nth-child(2) {
    opacity: ${props => props.isOpen ? '0' : '1'};
    transform: ${props => props.isOpen ? 'scaleX(0)' : 'scaleX(1)'};
  }
  
  &:nth-child(3) {
    transform: ${props => props.isOpen ? 'rotate(45deg) translate(-5px, -6px)' : 'translateX(0)'};
  }
`;

const MobileMenuOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1100;
  opacity: ${props => props.isOpen ? '1' : '0'};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  backdrop-filter: blur(4px);
  display: none;

  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenu = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 280px;
  height: 100vh;
  height: 100dvh; /* Dynamic viewport height for mobile */
  background: var(--color-white);
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
  z-index: 1110;
  display: none;
  flex-direction: column;
  transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow-y: auto;
  padding-top: env(safe-area-inset-top, 0);
  padding-bottom: env(safe-area-inset-bottom, 0);

  @media (max-width: 768px) {
    display: flex;
  }

  @media (max-width: 320px) {
    width: 100vw;
  }
`;

const MobileMenuHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-bottom: 2px solid var(--color-gray-100);
  background: var(--color-gray-50);
  min-height: 80px;
`;

const MobileMenuLogo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: var(--font-serif);
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-gray-800);
`;

const MobileMenuClose = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  border-radius: var(--border-radius-md);
  transition: background-color 0.2s ease;
  font-size: 1.5rem;

  &:hover {
    background: var(--color-gray-200);
  }
`;

const MobileMenuContent = styled.div`
  flex: 1;
  padding: 1rem 0;
  position: relative;
  overflow: visible;
`;

const MobileNavLink = styled(Link)`
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  color: var(--color-gray-700);
  text-decoration: none;
  font-weight: 500;
  font-family: var(--font-sans);
  border-bottom: 1px solid var(--color-gray-100);
  transition: all 0.2s ease;
  position: relative;

  &:hover, &:focus {
    background-color: var(--color-gray-50);
    color: var(--color-gray-900);
    padding-left: 2rem;
  }

  &:focus {
    outline: none;
    box-shadow: inset 3px 0 0 var(--color-primary);
  }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: var(--color-primary);
    transform: scaleY(0);
    transition: transform 0.2s ease;
  }

  &:hover::before {
    transform: scaleY(1);
  }
`;

const MobileMenuAction = styled.button`
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  background: none;
  border: none;
  color: var(--color-gray-700);
  font-weight: 500;
  font-family: var(--font-sans);
  text-align: left;
  width: 100%;
  border-bottom: 1px solid var(--color-gray-100);
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
  position: relative;

  &:hover, &:focus {
    background-color: var(--color-gray-50);
    color: var(--color-gray-900);
    padding-left: 2rem;
  }

  &:focus {
    box-shadow: inset 3px 0 0 var(--color-primary);
  }

  &:active {
    background-color: var(--color-gray-200);
  }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: var(--color-primary);
    transform: scaleY(0);
    transition: transform 0.2s ease;
  }

  &:hover::before {
    transform: scaleY(1);
  }
`;

const MobileLanguageSwitcher = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--color-gray-100);
  position: relative;
  overflow: visible;
  z-index: 1;
`;

const MobileMenuFooter = styled.div`
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--color-gray-100);
  background: var(--color-gray-50);
  text-align: center;
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
  margin-top: auto;
`;

const StyledNavLink = styled(Link)`
  color: var(--color-gray-700);
  text-decoration: none;
  font-weight: 500;
  font-family: var(--font-sans);
  transition: all 0.2s ease;
  white-space: nowrap;
  padding: 0.5rem 0.8rem;
  border-radius: var(--border-radius-md);
  background: transparent;
  border: 1px solid transparent;
  font-size: 0.95rem;

  &:hover {
    color: var(--color-primary);
    background: var(--color-white);
    border-color: var(--color-primary);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.4rem 0.8rem;
  }
`;

const UserMenu = styled.div`
  position: relative;
  display: inline-block;

  @media (max-width: 768px) {
    display: none; /* Hide user menu completely on mobile */
  }
`;

const UserMenuButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--color-gray-100);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--border-radius-md);
  cursor: pointer;
  font-family: var(--font-sans);
  color: var(--color-gray-700);
  font-weight: 500;
  transition: all 0.2s ease;
  outline: none;

  &:hover {
    background: var(--color-gray-200);
    border-color: var(--color-gray-400);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:focus {
    background: var(--color-gray-200);
    border-color: var(--color-primary);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    padding: 0.4rem 0.8rem;
    font-size: 0.85rem;
  }
`;

const UserMenuDropdown = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.25rem;
  background: var(--color-white);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--border-radius-md);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15), 0 4px 6px rgba(0, 0, 0, 0.1);
  min-width: 180px;
  z-index: 1120;
  opacity: ${props => props.isOpen ? '1' : '0'};
  transform: ${props => props.isOpen ? 'translateY(0) scale(1)' : 'translateY(-8px) scale(0.95)'};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.2s ease;
  backdrop-filter: blur(8px);

  @media (max-width: 768px) {
    display: none; /* Hide user dropdown on mobile - use mobile menu instead */
  }
`;

const UserMenuLink = styled(Link)`
  display: block;
  padding: 0.75rem 1rem;
  color: var(--color-gray-700);
  text-decoration: none;
  font-family: var(--font-sans);
  font-weight: 500;
  border-bottom: 1px solid var(--color-gray-100);
  transition: background-color 0.2s ease;

  &:hover {
    background: var(--color-gray-50);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const UserMenuButton2 = styled.button`
  width: 100%;
  text-align: left;
  padding: 0.75rem 1rem;
  background: none;
  border: none;
  color: var(--color-gray-700);
  font-family: var(--font-sans);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;
  border-radius: var(--border-radius-sm);
  margin: 2px;

  &:hover {
    background: var(--color-gray-50);
    color: var(--color-gray-900);
    transform: translateX(2px);
  }

  &:focus {
    background: var(--color-gray-100);
    box-shadow: inset 0 0 0 1px var(--color-primary);
  }

  &:active {
    background: var(--color-gray-200);
    transform: translateX(0);
  }
`;

const AuthButtons = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;

  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const Header: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { keycloak, user, login, register, logout } = useAuth();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [visibleItems, setVisibleItems] = useState<string[]>([]);
  const [overflowItems, setOverflowItems] = useState<string[]>([]);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const navigationRef = useRef<HTMLDivElement>(null);
  const moreMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
        setMoreMenuOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setUserMenuOpen(false);
        setMobileMenuOpen(false);
        setMoreMenuOpen(false);
      }
    };

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [userMenuOpen, moreMenuOpen]);

  // Handle body scroll lock for mobile menu
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${window.scrollY}px`;
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY.replace('-', '').replace('px', ''), 10));
      }
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [mobileMenuOpen]);

    const navItems = useMemo(() => {
        if (keycloak?.authenticated) {
            const items = [
                { key: 'dashboard', to: '/dashboard', label: t('navigation.dashboard') },
                { key: 'create', to: '/story/new', label: t('navigation.createStory') },
                { key: 'myStories', to: '/my-stories', label: t('dashboard.myStories') },
                { key: 'guidelines', to: '/guidelines', label: t('navigation.contentGuidelines') },
                { key: 'friends', to: '/friends', label: t('navigation.friends') },
            ];

            if (user?.roles?.some(role =>
                ['ADMIN', 'SUPER_ADMIN', 'MODERATOR', 'ANALYST'].includes(role.toUpperCase())
            )) {
                items.push({ key: 'admin', to: '/admin', label: `🛠️ ${t('navigation.admin', 'Admin')}` });
            }

            return items;
        } else {
            return [
                { key: 'browse', to: '/stories', label: t('navigation.browseStories') },
                { key: 'guidelines', to: '/guidelines', label: t('navigation.contentGuidelines') },
            ];
        }
    }, [keycloak?.authenticated, user?.roles, i18n.language, t]);

  // Overflow detection logic
  const checkOverflow = useCallback(() => {
    if (!navigationRef.current) return;

    const container = navigationRef.current;
    const containerWidth = container.offsetWidth;

    // For now, show all items and only hide them if container is very small
    // In the future, we can implement proper measurement-based overflow detection
    if (containerWidth < 400) {
      // On very small screens, show fewer items
      const visible = navItems.slice(0, Math.max(1, Math.floor(containerWidth / 120))).map(item => item.key);
      const overflow = navItems.slice(visible.length).map(item => item.key);
      setVisibleItems(visible);
      setOverflowItems(overflow);
    } else {
      // On normal screens, show all items
      setVisibleItems(navItems.map(item => item.key));
      setOverflowItems([]);
    }
  }, [navItems, i18n.language]);

  // Initialize visible items when navItems change
  useEffect(() => {
    if (navItems.length > 0 && visibleItems.length === 0) {
      setVisibleItems(navItems.map(item => item.key));
    }
  }, [navItems, visibleItems.length]);

  // Check overflow on mount and window resize
  useEffect(() => {
    checkOverflow();

    const handleResize = () => {
      checkOverflow();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [checkOverflow]);

  // Check overflow when language changes (text length changes)
  useEffect(() => {
    checkOverflow();
  }, [i18n.language, checkOverflow]);

  const handleLogin = () => {
    login();
  };

  const handleSignUp = () => {
    register();
  };

  const handleLogout = () => {
    setUserMenuOpen(false);
    logout();
  };

  const handleProfileClick = () => {
    setUserMenuOpen(false);
    navigate('/profile');
  };


  const handleMobileMenuClick = (action?: () => void) => {
    setMobileMenuOpen(false);
    if (action) action();
  };

  return (
    <>
      <HeaderContainer>
        <HeaderContent>
          {/* Desktop Menu Bar */}
          <MenuBar>
            <Logo to="/">
              <LogoIcon>📚</LogoIcon>
              BookKids
            </Logo>

            <Navigation ref={navigationRef} hasAuthButtons={!keycloak?.authenticated}>
              <NavigationItems>
                {/* Render visible navigation items */}
                {navItems
                  .filter(item => visibleItems.includes(item.key))
                  .map(item => (
                    <StyledNavLink key={item.key} to={item.to}>
                      {item.label}
                    </StyledNavLink>
                  ))
                }

                {/* Show "More" menu if there are overflow items */}
                {overflowItems.length > 0 && (
                  <MoreMenuContainer ref={moreMenuRef}>
                    <MoreMenuButton onClick={() => setMoreMenuOpen(!moreMenuOpen)}>
                      {t('navigation.more', 'More')} ▼
                    </MoreMenuButton>
                    <MoreMenuDropdown isOpen={moreMenuOpen}>
                      {navItems
                        .filter(item => overflowItems.includes(item.key))
                        .map(item => (
                          <MoreMenuLink
                            key={item.key}
                            to={item.to}
                            onClick={() => setMoreMenuOpen(false)}
                          >
                            {item.label}
                          </MoreMenuLink>
                        ))
                      }
                    </MoreMenuDropdown>
                  </MoreMenuContainer>
                )}
              </NavigationItems>

              {/* Fixed items that always show */}
              <RightItems>
                {keycloak?.authenticated ? (
                  <>
                    <NotificationBell />

                    <UserMenu ref={userMenuRef}>
                      <UserMenuButton
                        onClick={() => setUserMenuOpen(!userMenuOpen)}
                      >
                        {user?.fullName || user?.username || 'User'} ▼
                      </UserMenuButton>
                      <UserMenuDropdown isOpen={userMenuOpen}>
                        <UserMenuButton2 onClick={handleProfileClick}>
                          {t('navigation.profile')}
                        </UserMenuButton2>
                        <UserMenuButton2 onClick={handleLogout}>
                          {t('navigation.logout')}
                        </UserMenuButton2>
                      </UserMenuDropdown>
                    </UserMenu>

                    <LanguageSwitcher />
                  </>
                ) : (
                  <>
                    <LanguageSwitcher />
                    <AuthButtons>
                      <button className="btn btn-outline" onClick={handleLogin}>
                        {t('navigation.login')}
                      </button>
                      <button className="btn btn-primary" onClick={handleSignUp}>
                        {t('navigation.signUp')}
                      </button>
                    </AuthButtons>
                  </>
                )}
              </RightItems>
            </Navigation>
          </MenuBar>

          {/* Mobile Burger Button */}
          <MobileMenuButton onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <BurgerLine isOpen={mobileMenuOpen} />
            <BurgerLine isOpen={mobileMenuOpen} />
            <BurgerLine isOpen={mobileMenuOpen} />
          </MobileMenuButton>
        </HeaderContent>
      </HeaderContainer>

      {/* Mobile Menu */}
      <MobileMenuOverlay 
        isOpen={mobileMenuOpen} 
        onClick={() => setMobileMenuOpen(false)}
      />
      <MobileMenu isOpen={mobileMenuOpen}>
        <MobileMenuHeader>
          <MobileMenuLogo>
            📚 BookKids
          </MobileMenuLogo>
          <MobileMenuClose onClick={() => setMobileMenuOpen(false)}>
            ✕
          </MobileMenuClose>
        </MobileMenuHeader>

        <MobileMenuContent>
          {keycloak?.authenticated ? (
            <>
              <MobileNavLink to="/dashboard" onClick={() => handleMobileMenuClick()}>
                🏠 {t('navigation.dashboard')}
              </MobileNavLink>
              <MobileNavLink to="/story/new" onClick={() => handleMobileMenuClick()}>
                ✍️ {t('navigation.createStory')}
              </MobileNavLink>
              <MobileNavLink to="/my-stories" onClick={() => handleMobileMenuClick()}>
                📚 {t('dashboard.myStories')}
              </MobileNavLink>
              <MobileNavLink to="/guidelines" onClick={() => handleMobileMenuClick()}>
                📋 {t('navigation.contentGuidelines')}
              </MobileNavLink>
              <MobileNavLink to="/friends" onClick={() => handleMobileMenuClick()}>
                👥 {t('navigation.friends')}
              </MobileNavLink>
              <MobileNavLink to="/profile" onClick={() => handleMobileMenuClick()}>
                👤 {t('navigation.profile')}
              </MobileNavLink>
              
              {/* Mobile admin access */}
              {user?.roles?.some(role => 
                ['ADMIN', 'SUPER_ADMIN', 'MODERATOR', 'ANALYST'].includes(role.toUpperCase())
              ) && (
                <MobileNavLink to="/admin" onClick={() => handleMobileMenuClick()}>
                  🛠️ {t('navigation.admin', 'Admin Panel')}
                </MobileNavLink>
              )}
              
              <MobileLanguageSwitcher>
                <LanguageSwitcher />
              </MobileLanguageSwitcher>
              
              <MobileMenuAction onClick={() => handleMobileMenuClick(handleLogout)}>
                🚪 {t('navigation.logout')}
              </MobileMenuAction>
            </>
          ) : (
            <>
              <MobileNavLink to="/stories" onClick={() => handleMobileMenuClick()}>
                📚 {t('navigation.browseStories')}
              </MobileNavLink>
              <MobileNavLink to="/guidelines" onClick={() => handleMobileMenuClick()}>
                📝 {t('navigation.contentGuidelines')}
              </MobileNavLink>
              
              <MobileLanguageSwitcher>
                <LanguageSwitcher />
              </MobileLanguageSwitcher>
              
              <MobileMenuAction onClick={() => handleMobileMenuClick(handleLogin)}>
                🔑 {t('navigation.login')}
              </MobileMenuAction>
              <MobileMenuAction onClick={() => handleMobileMenuClick(handleSignUp)}>
                📝 {t('navigation.signUp')}
              </MobileMenuAction>
            </>
          )}
        </MobileMenuContent>

        <MobileMenuFooter>
          {user?.fullName || t('common.welcome')}
        </MobileMenuFooter>
      </MobileMenu>
    </>
  );
};

export default Header;